import { memo, useMemo } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Col,
  Combobox,
  Datepicker,
  type HookFormMethods,
  HookFormResolver,
  Number,
  Row,
  Text
} from '@dec/shared';
import { label } from 'app/modules/Taxpayer/TaxpayerTaxReturns/translation';
import { useTranslation } from 'react-i18next';

import { type CatalogValue } from '@/app/modules/configuration/catalogs/models/Catalog';

import { PAYMENT_STATES, type PaymentFilterForm, type PaymentOption } from './search';

interface PaymentFiltersProps {
  taxCollectors: Array<CatalogValue>;

  jurisdictions?: Array<CatalogValue>;

  byJurisdiction?: boolean;

  onSearch: (values: PaymentFilterForm) => void;

  onClear: () => void;
}

export const PaymentFilters = memo(({ taxCollectors, onSearch, onClear }: PaymentFiltersProps) => {
  const { t } = useTranslation();

  const stateOptions = useMemo<Array<PaymentOption>>(
    () =>
      PAYMENT_STATES.map((state) => ({
        id: state,
        name: t(`common.taxreturn-status.${state}`)
      })),
    [t]
  );

  const handleClear = (reset: HookFormMethods['reset']) => {
    reset({});

    onClear();
  };

  return (
    <HookFormResolver onSubmit={onSearch}>
      {({ reset }: HookFormMethods) => (
        <>
          <Row>
            <div className="col-12 col-md-6">
              <Combobox
                code="id"
                display="name"
                data={taxCollectors}
                label={t('taxpayer-payments.label.taxCollector')}
                name="taxCollector"
              />
            </div>

            <Col>
              <Text label={t('config.tin')} name="tin" />
            </Col>

            <Col>
              <Combobox
                code="id"
                display="name"
                data={stateOptions}
                label={label(t, 'taxreturnStatus')}
                name="status"
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <Datepicker label={t('common.label.dateFrom')} name="dateFrom" />
            </Col>

            <Col>
              <Datepicker label={t('common.label.dateTo')} name="dateTo" />
            </Col>

            <Col>
              <Number
                includeThousandsSeparator
                allowDecimal
                label={t('taxpayer-payments.label.amountFrom')}
                name="amountFrom"
              />
            </Col>

            <Col>
              <Number
                includeThousandsSeparator
                allowDecimal
                label={t('taxpayer-payments.label.amountTo')}
                name="amountTo"
              />
            </Col>
          </Row>

          <BtnToolbar>
            <BtnGroup>
              <Button
                action={ButtonAction.clean}
                variant="secondary"
                type="reset"
                onClick={() => handleClear(reset)}
              />
            </BtnGroup>

            <BtnGroup>
              <Button action={ButtonAction.find} type="submit" variant="primary" />
            </BtnGroup>
          </BtnToolbar>
        </>
      )}
    </HookFormResolver>
  );
});

PaymentFilters.displayName = 'PaymentFilters';
